import React from 'react';
import { Building2, ExternalLink, Mail, Phone, Users } from 'lucide-react';
import { FilingPanel } from './FilingPanel';

const ContactLine = ({ icon: Icon, children, href }) => (
  <li className="flex items-start gap-2 text-xs text-slate-600">
    <Icon className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" aria-hidden="true" />
    {href ? (
      <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="font-semibold text-[#008080] hover:underline break-all">
        {children}
      </a>
    ) : (
      <span className="break-words">{children}</span>
    )}
  </li>
);

const Intermediary = ({ title, entity, names }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
    <ul className="mt-1 space-y-0.5">
      {names.map((name) => (
        <li key={name} className="text-sm font-bold text-slate-900 leading-snug">
          {name}
        </li>
      ))}
    </ul>
    <ul className="mt-2 space-y-1">
      {entity.sebi && <ContactLine icon={Building2}>SEBI Reg. {entity.sebi}</ContactLine>}
      {entity.email && (
        <ContactLine icon={Mail} href={`mailto:${entity.email}`}>
          {entity.email}
        </ContactLine>
      )}
      {entity.phone && (
        <ContactLine icon={Phone} href={`tel:${entity.phone.replace(/\s/g, '')}`}>
          {entity.phone}
        </ContactLine>
      )}
      {entity.website && (
        <ContactLine icon={ExternalLink} href={entity.website}>
          {entity.website.replace(/^https?:\/\//, '')}
        </ContactLine>
      )}
      {entity.portal && (
        <ContactLine icon={ExternalLink} href={entity.portal}>
          Allotment status portal
        </ContactLine>
      )}
    </ul>
  </div>
);

/** Promoters, book running lead managers, registrar and the issuer's own contacts. */
export const FilingParties = ({ parties }) => {
  const { registrar, leadManager, promoters, registeredOffice, complianceOfficer, website, email, phone } = parties;
  const hasIssuer = registeredOffice || complianceOfficer || website || email || phone;
  if (!registrar && !leadManager && !promoters.length && !hasIssuer) return null;

  return (
    <FilingPanel id="filing-parties-heading" title="Issue Parties & Contacts" icon={Users}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {leadManager && <Intermediary title="Book running lead managers" entity={leadManager} names={leadManager.names} />}
        {registrar && <Intermediary title="Registrar to the issue" entity={registrar} names={[registrar.name]} />}

        {promoters.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Promoters</p>
            <ul className="mt-1 space-y-0.5">
              {promoters.map((name) => (
                <li key={name} className="text-sm font-bold text-slate-900 leading-snug">
                  {name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasIssuer && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Issuer</p>
            {registeredOffice && <p className="mt-1 text-xs text-slate-600 leading-relaxed">{registeredOffice}</p>}
            <ul className="mt-2 space-y-1">
              {complianceOfficer && <ContactLine icon={Users}>{complianceOfficer}</ContactLine>}
              {email && (
                <ContactLine icon={Mail} href={`mailto:${email}`}>
                  {email}
                </ContactLine>
              )}
              {phone && (
                <ContactLine icon={Phone} href={`tel:${phone.replace(/\s/g, '')}`}>
                  {phone}
                </ContactLine>
              )}
              {website && (
                <ContactLine icon={ExternalLink} href={website}>
                  {website.replace(/^https?:\/\//, '')}
                </ContactLine>
              )}
            </ul>
          </div>
        )}
      </div>
    </FilingPanel>
  );
};

export default FilingParties;
